/* import __COLOCATED_TEMPLATE__ from './guideline-header.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Guideline from 'embercom/models/ai-agent/guidelines';
import { type PulseAccordionSection } from 'glint/pulse';
import type AiAgentGuidelineService from 'embercom/services/ai-agent-guideline-service';
import type AttributeInfoResolver from 'embercom/lib/common/template-attribute-resolver';

enum Stat {
  USED = 'used',
  RESOLVED = 'resolved',
  ROUTED = 'routed',
}

interface Signature {
  Args: {
    guideline: Guideline;
    section: PulseAccordionSection;
    header: PulseAccordionSection['Header'];
    index: number;
    category: string;
    chartDefinitionsHash: Record<string, Record<Stat, any>>;
    isNewGuideline?: boolean;
  };
  Element: HTMLDivElement;
}

export default class GuidelineHeader extends Component<Signature> {
  @service declare aiAgentGuidelineService: AiAgentGuidelineService;
  @service declare permissionsService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;

  @tracked selectedGuideline?: Guideline;
  @tracked selectedStat?: Stat;

  stats = [Stat.USED, Stat.RESOLVED, Stat.ROUTED];

  drillInColumns = [
    'conversation.id',
    'conversation.first_user_conversation_part_created_at',
    'conversation.fin.resolution_state',
    'conversation.channel_type',
    'conversation.bot_conversation_rating.rating_index',
    'conversation.fin.guidelines_applied.guideline_ids',
  ];

  get canAdminAccessReporting(): boolean {
    return this.permissionsService.currentAdminCan('can_access_reporting');
  }

  get attributeInfoResolver(): AttributeInfoResolver {
    return this.aiAgentGuidelineService.attributeInfoResolver();
  }

  get showReporting() {
    if (!this.canAdminAccessReporting) {
      return false;
    }
    let excludedCategories = ['copilot', 'voice_clarification', 'voice_tone'];

    if (excludedCategories.includes(this.args.category)) {
      return false;
    }

    return true;
  }

  @action
  openSideDrawer(guideline: Guideline, stat: Stat, event: Event) {
    this.trackEvent(stat);
    event.stopPropagation();
    event.preventDefault();
    if (this.permissionsService.currentAdminCan('can_reporting__drillin__access')) {
      this.selectedGuideline = guideline;
      this.selectedStat = stat;
    } else {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_reporting__drillin__access',
      );
    }
  }

  @action
  closeSideDrawer() {
    this.selectedGuideline = undefined;
    this.selectedStat = undefined;
  }

  @action
  trackEvent(stat_clicked: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'guidance_stat',
      place: 'guidance_card',
      section: 'guidance',
      stat: stat_clicked,
    });
  }

  @action
  async cancelGuideline(guideline: Guideline) {
    guideline.rollbackAttributes();

    // If it's a saved guideline that is now empty (was just created or rolled back to empty),
    // delete it from the backend
    if (!guideline.isNew && !guideline.hasPrompt) {
      try {
        await guideline.destroyRecord();
      } catch (error) {
        console.error('Failed to delete empty guideline:', error);
      } finally {
        this.aiAgentGuidelineService.updatePreview();
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Guidance::GuidelineHeader': typeof GuidelineHeader;
    'ai-agent/guidance/guideline-header': typeof GuidelineHeader;
  }
}
